import { useState } from 'react';
import { Check, Package, Eye } from 'lucide-react';
import type { Product } from '../../types/product';
import { useTranslation } from '../../i18n';

interface ProductChipSelectorProps {
  products: Product[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export default function ProductChipSelector({
  products,
  selectedIds,
  onToggle,
  onSelectAll,
  onDeselectAll,
}: ProductChipSelectorProps) {
  const { t: strings } = useTranslation();
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const allSelected = selectedIds.size === products.length;
  const noneSelected = selectedIds.size === 0;

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {strings.cart.email.products}
          <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">
            {strings.cart.email.productsCount(selectedIds.size, products.length)}
          </span>
        </label>
        <button
          type="button"
          onClick={allSelected ? onDeselectAll : onSelectAll}
          className="text-xs font-medium text-vivatis-600 dark:text-vivatis-400 hover:text-vivatis-700 dark:hover:text-vivatis-300 transition-colors"
        >
          {allSelected ? strings.cart.email.deselectAll : strings.cart.email.selectAll}
        </button>
      </div>

      {/* Chip grid */}
      <div className="flex flex-wrap gap-1.5">
        {products.map((product) => {
          const isSelected = selectedIds.has(product.id);
          const isHovered = hoveredId === product.id;

          return (
            <div key={product.id} className="relative">
              <button
                type="button"
                onClick={() => onToggle(product.id)}
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`
                  group inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm
                  transition-all duration-200 ease-out cursor-pointer select-none
                  ${isSelected
                    ? 'bg-vivatis-50 dark:bg-vivatis-950/40 text-vivatis-700 dark:text-vivatis-300 border border-vivatis-200 dark:border-vivatis-800 shadow-sm'
                    : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 opacity-60 hover:opacity-80'
                  }
                `}
              >
                {/* Check / unchecked indicator */}
                <span className={`
                  w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors duration-200
                  ${isSelected
                    ? 'bg-vivatis-600 dark:bg-vivatis-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-600'
                  }
                `}>
                  {isSelected && <Check className="w-3 h-3" />}
                </span>

                {/* Product name */}
                <span className="truncate max-w-[14rem]">{product.name}</span>
              </button>

              {/* Hover popover — rendered below the chip */}
              {isHovered && (
                <div className="absolute z-30 top-full left-0 mt-2 w-64 pointer-events-none">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 text-left">
                    {/* Arrow pointing up */}
                    <div className="absolute -top-1 left-4 w-2 h-2 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 rotate-45" />
                    <div className="flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-vivatis-50 dark:bg-vivatis-950/40 flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4 text-vivatis-600 dark:text-vivatis-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">
                          {product.articleNumber}
                        </p>
                        {product.partner && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {product.partner.name}
                          </p>
                        )}
                        {product.category && (
                          <span
                            className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: product.category.color ? `${product.category.color}20` : undefined,
                              color: product.category.color ?? undefined,
                            }}
                          >
                            {product.category.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Warning when none selected */}
      {noneSelected && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Eye className="w-3.5 h-3.5 shrink-0" />
          {strings.cart.email.noProductsWarning}
        </p>
      )}
    </div>
  );
}
